/**
 * WaterwayPopup - Popup for rivers, streams, and lakes on the map.
 */
import { X, Navigation, Waves, Loader2, Activity, Anchor, MapPin } from 'lucide-react';

export default function WaterwayPopup({
  waterway,
  riverDataLoading,
  onClose,
}) {
  const rd = waterway.riverData;

  return (
    <div className="p-3 max-w-xs" data-testid="waterway-popup">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-base leading-tight">
            {rd?.display_name || waterway.name}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
              waterway.type === 'lake'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-cyan-100 text-cyan-700'
            }`}>
              <Waves className="w-3 h-3 mr-1" />
              {waterway.type === 'lake' ? 'Lake' : 'Stream/River'}
            </span>
            {riverDataLoading && (
              <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          data-testid="popup-close"
          aria-label="Close popup"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* River Stats */}
      {rd ? (
        <div className="space-y-2 mb-3">
          {rd.states?.length > 1 && (
            <div className="flex items-center gap-1 text-xs">
              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                {rd.states.join('/')}
              </span>
              {rd.is_boundary_river && (
                <span className="text-gray-400">Multi-state river</span>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3 text-sm text-gray-600">
            {rd.total_length_mi && (
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Total Length:</span>
                <span className="font-medium">{rd.total_length_mi.toFixed(1)} mi</span>
              </div>
            )}
            {rd.segment_count > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-gray-400">Segments:</span>
                <span className="font-medium">{rd.segment_count}</span>
              </div>
            )}
          </div>

          {rd.species?.length > 0 && (
            <div className="text-sm">
              <span className="text-gray-400">Species: </span>
              <span className="text-gray-700">{rd.species.join(', ')}</span>
            </div>
          )}

          {rd.regulations && (
            <div className="text-sm">
              <span className="text-gray-400">Regulations: </span>
              <span className="text-gray-700">{rd.regulations}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
          {waterway.length_mi && (
            <div className="flex items-center gap-1">
              <span className="text-gray-400">Segment Length:</span>
              <span className="font-medium">{waterway.length_mi} mi</span>
            </div>
          )}
          {waterway.area_sqkm && (
            <div className="flex items-center gap-1">
              <span className="text-gray-400">Area:</span>
              <span className="font-medium">{(waterway.area_sqkm * 0.386102).toFixed(2)} sq mi</span>
            </div>
          )}
        </div>
      )}

      {/* NHD ID */}
      <p className="text-xs text-gray-400 mb-2">
        {waterway.gnis_id ? (<>GNIS: {waterway.gnis_id} &middot; </>) : null}
        NHD: {waterway.permanent_identifier?.substring(0, 12)}...
      </p>

      {/* Nearby Stream Gauges */}
      {waterway.nearbyGauges?.length > 0 && (
        <div className="border-t border-gray-100 pt-2 mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Activity className="w-3 h-3" />
            Nearby Stream Gauges
          </p>
          <div className="space-y-1.5">
            {waterway.nearbyGauges.slice(0, 3).map((gauge) => {
              const cond = gauge.conditions;
              return (
                <div key={gauge.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded px-2 py-1.5">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    cond?.condition === 'high' || cond?.condition === 'dangerous' ? 'bg-red-500' :
                    cond?.condition === 'normal' ? 'bg-green-500' :
                    cond?.condition === 'low' || cond?.condition === 'very_low' ? 'bg-yellow-500' :
                    'bg-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-gray-700">{gauge.name?.replace(/,\s*[A-Z]{2}$/, '')}</p>
                    {cond ? (
                      <p className="text-gray-500">
                        {cond.discharge_cfs != null && <span>{cond.discharge_cfs} cfs</span>}
                        {cond.gage_height_ft != null && <span> · {cond.gage_height_ft} ft</span>}
                        {cond.condition_label && <span className="ml-1 text-gray-400">({cond.condition_label})</span>}
                      </p>
                    ) : (
                      <p className="text-gray-400">Loading...</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Nearby Access Points */}
      {waterway.nearbyPois?.length > 0 && (
        <div className="border-t border-gray-100 pt-2 mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5 flex items-center gap-1">
            <Anchor className="w-3 h-3" />
            Nearby Access Points
          </p>
          <div className="flex flex-wrap gap-1">
            {waterway.nearbyPois.slice(0, 5).map((poi) => (
              <span key={poi.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-50 rounded-full text-gray-600">
                <MapPin className="w-2.5 h-2.5 text-gray-400" />
                <span className="truncate max-w-[120px]">{poi.name}</span>
                <span className="text-gray-400 text-[10px]">{poi.type?.replace(/_/g, ' ')}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Directions */}
      <button
        onClick={() => {
          const { lat, lng } = waterway.coordinates;
          window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
        }}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 transition-colors"
        data-testid="waterway-directions-btn"
      >
        <Navigation className="w-4 h-4" />
        Get Directions
      </button>
    </div>
  );
}
